import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle, SuggestModifiersChecker, useAssessmentColumns } from '../comps';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { queryCache } from 'react-query';
import Button from 'src/components/form/Button';
import { Guard } from '../Guard';
import { useScenarioColumns } from './useScenarioColumns';
import GenericTable from 'src/components/GenericTable';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { Back } from 'src/assets/dynamicSvg';
import { useAuth } from 'src/state/auth';
import {
  useAssessmentsQuery,
  useLinkFrameworkControlsToScenariosMutation,
  useControlsRmpQuery,
} from 'src/graphql/_generated';
import { useCache } from 'src/graphql';
import { isQueryLoading } from 'src/graphql';

const StyledModal = styled(Modal)`
  width: 1350px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  .back {
    display: flex;
    align-items: center;
    cursor: pointer;

    &__text {
      font-size: 18px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.cflowerBlue};
      margin-left: 13px;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }
`;

interface LinkScenariosToControlModalProps {
  onClose: () => void;
  controlId: string;
  subTitle?: {
    title: string;
    value: string;
  };
  isModifierStrategyType: boolean;
}

export const LinkScenariosToControlModal: React.FC<LinkScenariosToControlModalProps> = ({
  onClose,
  controlId,
  subTitle,
  isModifierStrategyType,
}) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [addRecommendedModifiers, setAddRecommendedModifiers] = useState(false);
  const { features, workspaceId } = useAuth();
  const { refetchQuery } = useCache();

  const { data: { assessments = [] } = {}, loading: isAssessmentsLoading } = useAssessmentsQuery();

  const {
    data: { controls: controlsRmp = [] } = {},
    loading: controlsRmpLoading,
    networkStatus: controlsRmpNetworkStatus,
  } = useControlsRmpQuery({
    variables: { workspaceId: workspaceId },
  });

  const isControlsRmpLoading = isQueryLoading({
    loading: controlsRmpLoading,
    data: controlsRmp,
    networkStatus: controlsRmpNetworkStatus,
  });

  // Extract linked scenario IDs from the control's risk management plans
  const linkedScenarioIds = useMemo(() => {
    const control = controlsRmp.find((c) => c.id === controlId);
    if (!control) return [];

    return control.riskManagementPlans.map((plan) => plan.scenario?.id).filter(Boolean) as string[];
  }, [controlsRmp, controlId]);

  const {
    columns: columnsAssessment,
    GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedAssessment,
    selectedAssessment,
    setSelectedAssessment,
  } = useAssessmentColumns({
    data: assessments,
  });

  const { columns, GTColumnSelectAnchorExported, checked, resetChecked } = useScenarioColumns({
    data: assessments.find((el) => el.id === selectedAssessment)?.scenarios || [],
    linkedScenarios: linkedScenarioIds,
    controlId,
  });

  const [linkFrameworkControlsToScenarios, { loading }] = useLinkFrameworkControlsToScenariosMutation({
    onCompleted: () => {
      queryCache.invalidateQueries();
      refetchQuery({ include: ['controls', 'control', 'filtersFor'] });
      onClose();
    },
    onError: (err: any) => setErr(err.message || 'Something went wrong'),
  });

  const isModalLoading = isAssessmentsLoading || isControlsRmpLoading;

  const isDisabledModSuggest = useMemo(() => {
    return !checked.length;
  }, [checked]);

  useEffect(() => {
    if (isDisabledModSuggest && addRecommendedModifiers) {
      setAddRecommendedModifiers(false);
    }
  }, [isDisabledModSuggest, addRecommendedModifiers, setAddRecommendedModifiers]);

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={!!checked.length}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false}>
          <Spacer $px={40} />

          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {selectedAssessment ? 'Select one or more scenarios to link' : 'Select Assessment'}
            </Poppins>
            {subTitle && <ModalSubTitle title={subTitle.title} value={subTitle.value} />}

            {selectedAssessment ? (
              <>
                <Spacer $px={10} />
                <div
                  className="back"
                  onClick={() => {
                    setSelectedAssessment('');
                    resetChecked();
                  }}
                >
                  <Back />
                  <Poppins className="back__text">Back</Poppins>
                </div>
                <Spacer $px={20} />
              </>
            ) : (
              <Spacer $px={20} />
            )}

            {selectedAssessment ? (
              <>
                <GenericTable
                  tableId={TableIds.controlLinkScenarioScenario}
                  data={assessments.find((el) => el.id === selectedAssessment)?.scenarios || []}
                  columns={columns}
                  GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                  itemHeight={50}
                  searchable={['name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at']}
                  rowDisabled={(row) => linkedScenarioIds.includes(row.id)}
                />
                {features['app.ai.suggestModifiers'] && isModifierStrategyType && (
                  <SuggestModifiersChecker
                    isDisabledModSuggest={isDisabledModSuggest}
                    addRecommendedModifiers={addRecommendedModifiers}
                    setAddRecommendedModifiers={setAddRecommendedModifiers}
                  />
                )}
              </>
            ) : (
              <>
                {!isModalLoading ? (
                  <GenericTable
                    tableId={TableIds.controlLinkScenarioAssessment}
                    data={assessments}
                    columns={columnsAssessment}
                    GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedAssessment}
                    itemHeight={50}
                    rowDisabled={(row) => row.scenarios.length === 0}
                    searchable={['name', 'description']}
                  />
                ) : (
                  <TableSkeleton />
                )}
              </>
            )}
          </div>

          {selectedAssessment && (
            <>
              <Spacer $px={30} />
              <div className="h-padding">
                <Button
                  disabled={loading || !checked.length}
                  onClick={() => {
                    linkFrameworkControlsToScenarios({
                      variables: {
                        frameworkControlScenarioLinks: checked.map((id) => ({
                          frameworkControlId: controlId,
                          scenarioId: id,
                        })),
                        addRecommendedModifiers,
                      },
                    });

                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Confirm',
                      modal: 'Link scenarios to control modal',
                    });
                  }}
                  primary
                  css="width: 100%;"
                  data-cy="submit-btn"
                >
                  CONFIRM
                </Button>

                {!!err && (
                  <>
                    <Spacer $px={15} />
                    <div className="error">{err}</div>
                  </>
                )}
              </div>
            </>
          )}
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
